import type { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { isValidIban } from './iban';
import type { Option } from './helpers';

export const dateValidator = (): ValidatorFn => (control: AbstractControl) =>
  !control.value ? { date: { value: control.value } } : null;

export const maxDateValidator = (maxAllowedDate: Date): ValidatorFn =>
  createDateComparisonValidator(
    maxAllowedDate,
    'maxDate',
    (date, referenceDate) => date > referenceDate
  );

export const minDateValidator = (minAllowedDate: Date): ValidatorFn =>
  createDateComparisonValidator(
    minAllowedDate,
    'minDate',
    (date, referenceDate) => date < referenceDate
  );

const createDateComparisonValidator =
  (
    referenceDate: Date,
    errorKey: string,
    compareFn: (date: Date, referenceDate: Date) => boolean
  ): ValidatorFn =>
  (control: AbstractControl) => {
    if (!control.value) return null;
    const date = new Date(control.value);
    return compareFn(date, referenceDate) ? { [errorKey]: { value: referenceDate } } : null;
  };

export function postalCodeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value?.trim()) return null;

    const validPLZ = /^[0-9]{5}$/;
    return !validPLZ.test(value) ? { invalidPostalCode: true } : null;
  };
}

export function integerValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const regex = /^-?\d+$/;

    return regex.test(control.value) ? null : { invalidInteger: true };
  };
}

export function phoneNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value?.trim()) return null;

    const validCharacters = /^[+]?[0-9\s()-]*$/;
    const validPlusPosition = value.indexOf('+') <= 0;
    const digitsOnly = value.replace(/\D/g, '');
    const hasValidLength = digitsOnly.length >= 6;

    const errors: ValidationErrors = {};

    if (!validCharacters.test(value) || !validPlusPosition) {
      errors['invalidPhoneNumber'] = true;
    }

    if (!hasValidLength) {
      errors['incompletePhoneNumber'] = true;
    }
    return Object.keys(errors).length > 0 ? errors : null;
  };
}

/**
 * Covers the country-specific length and structure plus the Mod-97 check digits, and
 * doubles as the gate for the backend call (see setUpIbanValidation).
 */
export function ibanValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    return isValidIban(value) ? null : { iban: true };
  };
}

/**
 * Rejects a value that is not one of the allowed enum members, so a step form restored from
 * storage cannot carry a value the Sparte no longer offers into the Berechnung. Pass the generated
 * enum object or the `Option` list the control is rendered from.
 */
export function enumValidator(
  enumObj: Record<string, string | number> | Option<string | number>[]
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string | number | null | undefined = control.value;
    if (value == null || value === '') return null;

    const allowed = Array.isArray(enumObj)
      ? enumObj.map(item => item.value)
      : Object.values(enumObj);

    return allowed.includes(value) ? null : { invalidEnumValue: { allowed, actual: value } };
  };
}

/**
 * Closes the gap Angular's `Validators.required` leaves open: it only checks for emptiness, so a
 * single space passes it. Reuses the `required` error key, so the existing message still applies.
 */
export function nonBlankValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (typeof value !== 'string' || !value.length) return null;

    return value.trim().length ? null : { required: true };
  };
}
